import React, { FC, useState, useCallback, useEffect } from 'react'
import { LeftCircleOutlined, RightCircleOutlined } from '@ant-design/icons'
import { Spin, Title, Text } from 'components/common'
import { useNumberOfDiscountAppliedQuery } from 'lib/graphql/storeadmin'

/**
 * 指定した月数後の1日を'YYYY-MM-DD'形式で取得する
 */
const getFirstDayOfMonth = (monthsToAdd = 0): string => {
  const now = new Date()
  const targetMonth = now.getMonth() + monthsToAdd
  const targetDate = new Date(now.getFullYear(), targetMonth, 1)

  const year = targetDate.getFullYear()
  const month = (targetDate.getMonth() + 1).toString().padStart(2, '0')
  const day = targetDate.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

type Props = {
  id?: string
}

const RemainingDiscountCard: FC<Props> = ({ id }) => {
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState<string>(
    getFirstDayOfMonth()
  )
  const [monthsOffset, setMonthsOffset] = useState(0)
  const [displayMonth, setDisplayMonth] = useState(new Date().getMonth() + 1)

  const { data, loading, refetch } = useNumberOfDiscountAppliedQuery({
    variables: {
      id,
      date: selectedDayOfMonth,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  /**
   * 選択された月を更新し、クエリを再取得する
   */
  const updateSelectedMonth = useCallback(
    async (increment: boolean) => {
      if (loading || monthsOffset < 0 || monthsOffset > 2) return

      const newMonthsOffset = increment ? monthsOffset + 1 : monthsOffset - 1
      setMonthsOffset(newMonthsOffset)

      // 月の更新
      let newDisplayMonth = displayMonth + (increment ? 1 : -1)
      if (newDisplayMonth > 12) {
        newDisplayMonth = 1 // 12月の次は1月
      } else if (newDisplayMonth < 1) {
        newDisplayMonth = 12 // 1月の前は12月
      }
      setDisplayMonth(newDisplayMonth)

      setSelectedDayOfMonth(getFirstDayOfMonth(newMonthsOffset))
    },
    [loading, monthsOffset, displayMonth]
  )

  useEffect(() => {
    if (!loading && id) {
      refetch()
    }
  }, [selectedDayOfMonth, refetch, loading, id])

  return (
    <div>
      <Title level={5} className="mb-3">
        月別減免情報
      </Title>

      <div className="grid grid-cols-3 py-4 w-1/2">
        <div>
          {monthsOffset !== 0 && (
            <button
              onClick={() => updateSelectedMonth(false)}
              className="hover:opacity-60 transition- duration-300"
            >
              <LeftCircleOutlined />
            </button>
          )}
        </div>
        <div>
          <Text strong>{displayMonth.toString()}月</Text>
        </div>
        <div>
          {monthsOffset !== 2 && (
            <button
              onClick={() => updateSelectedMonth(true)}
              className="hover:opacity-60 transition- duration-300"
            >
              <RightCircleOutlined />
            </button>
          )}
        </div>
      </div>
      <div className="w-1/2">
        <Spin spinning={loading} tip="Loading...">
          <div className="flex space-x-4">
            {data?.numberOfDiscountApplied ? (
              <>
                <Text type="secondary">減免残回数:</Text>
                <div className="flex space-x-1">
                  <Text strong>{data.numberOfDiscountApplied}</Text>
                  <div>回</div>
                </div>
              </>
            ) : (
              <Text type="secondary">適用されていません</Text>
            )}
          </div>
        </Spin>
      </div>
    </div>
  )
}

export default RemainingDiscountCard
